/**
 * Owns ECharts-only lifecycle concerns for a Flow composition: event listeners, chart connection
 * and action dispatch. It deliberately leaves dashboard state and derived data to the state service.
 * Required to handle data zoom, legend clicks and `echarts.connect` for the Flow and Net charts.
 */
import * as echarts from 'echarts';
import type { ECharts } from 'echarts';
import { Subscription } from 'rxjs';
import { DashboardService } from 'src/app/services/dashboard/dashboard.service';

export interface FlowDashboardChartInteractionCallbacks {
    onDataZoom: (params: any) => void;
    onLegendSelectChanged: (event: {
        selected: Record<string, boolean>;
    }) => void;
    onFullscreenExit: () => void;
}

/** Owns ECharts event wiring and the lifecycle of the flow/net connect group. */
export class FlowDashboardChartInteractions {
    private readonly wired = new Set<ECharts>();
    private flowChart?: ECharts;
    private netChart?: ECharts;
    private connectGroup = '';
    private showNetChart = true;
    private fullscreenSub?: Subscription;

    constructor(
        private dashboard: DashboardService,
        private callbacks: FlowDashboardChartInteractionCallbacks
    ) {}

    configure(instanceId: string, showNetChart: boolean) {
        if (this.connectGroup) {
            echarts.disconnect(this.connectGroup);
        }
        this.connectGroup = `${instanceId}.group`;
        this.showNetChart = showNetChart;
        this.fullscreenSub?.unsubscribe();
        this.fullscreenSub = this.dashboard.activeFullscreenItemId$.subscribe(
            (id) => this.onFullscreenChange(id)
        );
        this.maybeConnect();
    }

    wire(instance: ECharts, role: 'flow' | 'net') {
        if (role === 'flow') {
            this.flowChart = instance;
            if (!this.wired.has(instance)) {
                this.wired.add(instance);
                instance.on('datazoom', this.callbacks.onDataZoom);
                instance.on('legendselectchanged', (event: any) =>
                    this.callbacks.onLegendSelectChanged(event)
                );
            }
        } else {
            this.netChart = instance;
        }
        this.maybeConnect();
    }

    /** Keep programmatic breakdown toggles in sync with ECharts' native legend state. */
    dispatchFlowAction(action: { type: string; name: string }) {
        this.flowChart?.dispatchAction(action);
    }

    destroy() {
        this.fullscreenSub?.unsubscribe();
        if (this.connectGroup) {
            echarts.disconnect(this.connectGroup);
        }
    }

    private maybeConnect() {
        if (!this.showNetChart || !this.flowChart || !this.netChart) {
            return;
        }
        this.flowChart.group = this.connectGroup;
        this.netChart.group = this.connectGroup;
        echarts.connect(this.connectGroup);
    }

    private onFullscreenChange(activeId: string | null) {
        if (!this.showNetChart || !this.connectGroup) {
            return;
        }
        if (activeId) {
            echarts.disconnect(this.connectGroup);
        } else {
            this.maybeConnect();
            this.callbacks.onFullscreenExit();
        }
    }
}
